import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ThumbsUp, Share2, Globe, Image as ImageIcon } from 'lucide-react';

// Renders a generated post inside an actual Instagram/Facebook post card --
// not a plain text block -- so what you see is what would appear in-feed.
// Engagement numbers are illustrative placeholders (clearly fake, not real
// stats) purely for frame realism, same spirit as a design mockup.
const SocialPostCard = ({ post, brandKit, businessName }) => {
  const name = brandKit?.business_name || businessName || 'Your Business';
  const logo = brandKit?.logos?.primary;
  const initial = name.slice(0, 1).toUpperCase();
  const hashtagLine = (post.hashtags || []).map((h) => `#${h}`).join(' ');

  const Avatar = ({ size = 'w-8 h-8' }) => (
    <div className={`${size} rounded-full flex items-center justify-center overflow-hidden shrink-0 bg-primary/10`}>
      {logo ? <img src={logo} alt="" className="w-full h-full object-contain" /> : <span className="text-xs font-bold text-primary">{initial}</span>}
    </div>
  );

  const ImageBlock = () => (
    <div className="aspect-square bg-black/[0.04] flex items-center justify-center overflow-hidden">
      {post.image_url ? (
        <img src={post.image_url} alt={post.caption} className="w-full h-full object-cover" />
      ) : (
        <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
      )}
    </div>
  );

  if (post.platform === 'instagram') {
    return (
      <div className="border border-border bg-white max-w-sm w-full" data-testid={`social-post-${post.id}`}>
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Avatar />
            <div>
              <p className="text-xs font-semibold leading-tight">{name}</p>
              <p className="text-[10px] text-muted-foreground leading-tight">Sponsored</p>
            </div>
          </div>
          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
        </div>
        <ImageBlock />
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-3.5 mb-2">
            <Heart className="w-5 h-5" />
            <MessageCircle className="w-5 h-5" />
            <Send className="w-5 h-5" />
            <Bookmark className="w-5 h-5 ml-auto" />
          </div>
          <p className="text-xs font-semibold mb-1">1,248 likes</p>
          <p className="text-xs leading-relaxed">
            <span className="font-semibold">{name.toLowerCase().replace(/\s+/g, '')}</span> {post.caption}
          </p>
          {hashtagLine && <p className="text-xs text-blue-800/70 mt-1">{hashtagLine}</p>}
          <p className="text-[10px] text-muted-foreground mt-1.5 uppercase tracking-wide">2 hours ago</p>
        </div>
      </div>
    );
  }

  // facebook
  return (
    <div className="border border-border bg-white max-w-sm w-full" data-testid={`social-post-${post.id}`}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Avatar size="w-9 h-9" />
          <div>
            <p className="text-xs font-semibold leading-tight">{name}</p>
            <p className="text-[10px] text-muted-foreground leading-tight flex items-center gap-1">
              Sponsored · <Globe className="w-2.5 h-2.5" />
            </p>
          </div>
        </div>
        <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="px-3 pb-2.5">
        <p className="text-xs leading-relaxed">{post.caption}</p>
        {hashtagLine && <p className="text-xs text-blue-800/70 mt-1">{hashtagLine}</p>}
      </div>
      <ImageBlock />
      <div className="px-3 py-2 flex items-center justify-between text-[10px] text-muted-foreground border-b border-border">
        <span>124 · 8 comments · 3 shares</span>
      </div>
      <div className="px-1 py-1 flex items-center justify-around">
        <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground font-medium">
          <ThumbsUp className="w-3.5 h-3.5" /> Like
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground font-medium">
          <MessageCircle className="w-3.5 h-3.5" /> Comment
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground font-medium">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>
    </div>
  );
};

export default SocialPostCard;
